import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getResults, ResultCard, raceLink, fmtTime } from '../api/feeds';
import { betfairErrorMessage } from '../api/betfair';
import './Feeds.css';

const RESULTS_PER_PAGE = 20;

const Results: React.FC = () => {
  const [results, setResults] = useState<ResultCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const load = useCallback(async (resetPage = true) => {
    setError(null);
    try {
      setResults(await getResults(200));
      if (resetPage) setCurrentPage(1);
    } catch (err) {
      setError(betfairErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load, then auto-refresh every 30s so newly-settled races and new
  // meetings appear without a manual reload. Background refreshes keep the
  // user's current page (only the initial/manual load resets to page 1).
  useEffect(() => {
    load();
    const refetch = setInterval(() => load(false), 30000);
    return () => clearInterval(refetch);
  }, [load]);

  // Pagination (mirrors the Dashboard).
  const totalPages = Math.ceil(results.length / RESULTS_PER_PAGE);
  const indexOfLast = currentPage * RESULTS_PER_PAGE;
  const indexOfFirst = indexOfLast - RESULTS_PER_PAGE;
  const currentResults = results.slice(indexOfFirst, indexOfLast);

  const paginate = (page: number) => setCurrentPage(page);
  const prevPage = () => setCurrentPage((p) => Math.max(1, p - 1));
  const nextPage = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  return (
    <div className="feed">
      <div className="feed-header">
        <div>
          <h1>Results</h1>
          <div className="feed-sub">Settled races · winners &amp; Betfair starting prices</div>
        </div>
        <button className="feed-refresh" onClick={() => load()} disabled={loading}>↻ Refresh</button>
      </div>

      {error && <div className="feed-error">{error}</div>}

      {loading && results.length === 0 ? (
        <div className="feed-loading">Loading results…</div>
      ) : results.length === 0 ? (
        <div className="feed-empty">
          <p>No settled races yet. Results appear here once Betfair markets close.</p>
          <Link to="/">Browse all meetings →</Link>
        </div>
      ) : (
        <div className="feed-table-wrap">
          <table className="feed-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Meeting</th>
                <th>Race</th>
                <th>Winner</th>
                <th>BSP / Result</th>
                <th>Matched</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {currentResults.map((r) => (
                <tr key={r.market_id}>
                  <td>{r.date} <span style={{ color: '#9aa2ac' }}>{fmtTime(r.start_time)}</span></td>
                  <td>{r.meeting}</td>
                  <td>R{r.race_number}</td>
                  <td className="winner-cell">
                    {r.winner ? (
                      <>
                        <span className="winner-medal">🏆</span>
                        {r.winner.runner_name}
                      </>
                    ) : r.in_play ? (
                      <span className="winner-inplay" title="Race in progress — result not yet settled">🔴 In-play</span>
                    ) : r.status === 'SUSPENDED' ? (
                      <span className="winner-suspended" title="Betfair betting suspended — result not yet settled">⚠ Suspended</span>
                    ) : (
                      <span className="winner-pending">Result pending</span>
                    )}
                  </td>
                  <td>
                    {r.winner?.result_price != null ? (
                      <span className="result-price">{r.winner.result_price.toFixed(2)}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    {r.total_matched > 0
                      ? `$${r.total_matched.toLocaleString('en-AU', { maximumFractionDigits: 0 })}`
                      : '—'}
                  </td>
                  <td>
                    <Link className="feed-link" to={raceLink(r.meeting, r.race_number, r.date, 'results')}>
                      View →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="pagination">
              <div className="pagination-info">
                Showing {indexOfFirst + 1}-{Math.min(indexOfLast, results.length)} of {results.length} races
              </div>

              <div className="pagination-controls">
                <button onClick={prevPage} disabled={currentPage === 1} className="pagination-btn">
                  Previous
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNumber: number;
                  if (totalPages <= 5) {
                    pageNumber = i + 1;
                  } else if (currentPage <= 3) {
                    pageNumber = i + 1;
                  } else if (currentPage > totalPages - 3) {
                    pageNumber = totalPages - 4 + i;
                  } else {
                    pageNumber = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNumber}
                      onClick={() => paginate(pageNumber)}
                      className={`pagination-btn ${currentPage === pageNumber ? 'active' : ''}`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}

                <button onClick={nextPage} disabled={currentPage === totalPages} className="pagination-btn">
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Results;
